import { DateTime, Interval } from 'luxon'
import { sortBy, groupBy } from 'lodash'
import { CoachProfile, VideoCallSlot } from 'src/graphql.generated'
import { CallBookingParticipantFragment } from '../videoCallBookingHeaderQueries.generated'
import { useCoachAvailabilityVideoCallSlotsByParticipantQuery } from './coachAvailabilityVideoCallSlotsByParticipantQuery.generated'

interface Props {
  participant: CallBookingParticipantFragment
}

export type CoachAvailabilityDay = {
  dayId: string // Represents uniqueness of one day, format: YYYY-MM-DD
  slots: CoachProfileWithVideoCallSlots[]
}

export type CoachProfileWithVideoCallSlots = {
  coachProfile: CoachProfile
  videoCallSlot: CoachAvailabilityVideoCallSlot[]
}

export type CoachAvailabilityVideoCallSlot = Omit<VideoCallSlot, 'start' | 'end'> & {
  start: DateTime
  end: DateTime
}

export const coachAvailabilityVideoCallSlotEquals = (
  a: CoachAvailabilityVideoCallSlot,
  b: CoachAvailabilityVideoCallSlot
): boolean =>
  a.start.equals(b.start) &&
  a.end.equals(b.end) &&
  a.availabilityCalendarEventID === b.availabilityCalendarEventID &&
  a.coachEmail === b.coachEmail

export const useCoachAvailabilityDays = ({
  participant,
}: Props): { loading: boolean; coachAvailabilityDays: CoachAvailabilityDay[] } => {
  const { data, loading } = useCoachAvailabilityVideoCallSlotsByParticipantQuery({
    variables: { uid: participant.uid },
  })

  if (loading) {
    return { loading: true, coachAvailabilityDays: [] }
  }

  const { coachProfiles: rawCoachProfiles, videoCallSlots: rawVideoCallSlots } =
    data?.coachAvailabilityVideoCallSlotsByParticipant || {}
  if (!rawVideoCallSlots || !rawCoachProfiles) {
    return { loading: false, coachAvailabilityDays: [] }
  }

  const patientTimezone = participant.privateUserData.timezone ?? 'America/Los_Angeles'
  const videoCallSlots = rawVideoCallSlots?.map((slot) => ({
    ...slot,
    start: DateTime.fromJSDate(slot.start, { zone: patientTimezone }),
    end: DateTime.fromJSDate(slot.end, { zone: patientTimezone }),
  }))

  const coachAvailabilityDays = resolveCoachAvailabilityDays(videoCallSlots, rawCoachProfiles)

  return { coachAvailabilityDays, loading }
}

const resolveCoachAvailabilityDays = (
  videoCallSlots: CoachAvailabilityVideoCallSlot[],
  coachProfiles: CoachProfile[]
): CoachAvailabilityDay[] => {
  if (!videoCallSlots || videoCallSlots.length === 0 || !coachProfiles || coachProfiles.length === 0) {
    return []
  }

  // Get the range of dates from first to last slot
  const sortedSlots = sortBy(videoCallSlots, 'start')
  const firstSlot = sortedSlots[0]
  const lastSlot = sortedSlots[sortedSlots.length - 1]

  const slotInterval = Interval.fromDateTimes(firstSlot.start.startOf('day'), lastSlot.start.endOf('day'))

  // Group slots by day
  const slotsByDay = groupBy(videoCallSlots, (slot) => slot.start.startOf('day').toISODate())

  const coachProfilesMap = new Map(coachProfiles.map((profile) => [profile.email, profile]))

  return (
    slotInterval
      .splitBy({ days: 1 })
      // Removes any intervals that don't have a start date (theoretically this should never happen)
      .filter((interval) => !!interval.start)
      .map((interval) => {
        const dayId = interval.start!.startOf('day').toISODate()!
        const slotsForDay = slotsByDay[dayId] || []

        // Group slots by coach email for this day
        const slotsByCoach = groupBy(slotsForDay, 'coachEmail')

        const coachesWithSlots: CoachProfileWithVideoCallSlots[] = []

        Object.entries(slotsByCoach).forEach(([coachEmail, coachSlots]) => {
          const coachProfile = coachProfilesMap.get(coachEmail)
          if (coachProfile) {
            coachesWithSlots.push({
              coachProfile,
              videoCallSlot: coachSlots,
            })
          }
        })

        return {
          dayId,
          slots: coachesWithSlots, // This will be empty array for days without slots
        }
      })
  )
}
